import React from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ParentNavigationLink } from '@/components/ui/parent-navigation-link'
import { useFormatters } from '@/hooks/use-formatters'
import type { TransactionHeaderSummary } from '@/types/api/engagement'

interface DuplicateTransactionWarningDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onContinue: () => void
  existingTransactions: TransactionHeaderSummary[]
}

export function DuplicateTransactionWarningDialog({
  open,
  onOpenChange,
  onContinue,
  existingTransactions,
}: DuplicateTransactionWarningDialogProps) {
  const { formatCurrency, formatTimestamp } = useFormatters()
  
  const handleContinue = () => {
    onContinue()
    onOpenChange(false)
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Duplicate Billing Transaction Detected</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                A billing transaction already exists with the same Milestone Code, Engagement, and Amount.
              </p>
              <p>
                Creating another may result in duplicate billing.
              </p>
              <p>
                Would you like to continue?
              </p>
              
              {existingTransactions.length > 0 && (
                <div className="pt-2">
                  <p className="font-semibold mb-2">{existingTransactions.length} Existing Transaction(s):</p>
                  <div className="border rounded-md max-h-[200px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Transaction</TableHead>
                          <TableHead>Actioned by</TableHead>
                          <TableHead>Date and Time</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {existingTransactions.map((transaction) => (
                          <TableRow key={transaction.id}>
                            <TableCell>
                              <ParentNavigationLink
                                route={`/next/transaction/${transaction.id}/summary`}
                                className="text-primary hover:underline font-medium"
                              >
                                {transaction.transactionNumber}
                              </ParentNavigationLink>
                            </TableCell>
                            <TableCell>{transaction.createdByUserName}</TableCell>
                            <TableCell>
                              {formatTimestamp(transaction.createdDateTime)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(transaction.subtotal)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleContinue}>Continue Anyway</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

